import { APP_BASE_HREF, DatePipe, DecimalPipe, PercentPipe, registerLocaleData, TitleCasePipe } from '@angular/common';
import { provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import localeDa from '@angular/common/locales/da';
import localeEnGB from '@angular/common/locales/en-GB';
import localeFo from '@angular/common/locales/fo';
import {
  DEFAULT_CURRENCY_CODE,
  EnvironmentProviders,
  importProvidersFrom,
  LOCALE_ID,
  Provider,
  signal,
} from '@angular/core';
import { provideAnimations } from '@angular/platform-browser/animations';
import { ServiceWorkerModule } from '@angular/service-worker';
import type { EnvironmentResponse } from '@cassiopeia/integration';
import {
  AppInitializerProviders,
  CASSIOPEIA_CONFIG,
  CassiopeiaConfig,
  CassiopeiaServiceProviders,
  CUSTOMER_ENVIRONMENT,
  Environment,
  MemberStore,
  provideBannerStore,
  SsoTransferService,
} from '@web/service';
import { initializeTelemetry, provideTelemetry } from '@web/telemetry';

const registerCassiopeiaLocaleData = () => {
  registerLocaleData(localeDa);
  registerLocaleData(localeFo);
  registerLocaleData(localeEnGB);
};

registerCassiopeiaLocaleData();

export interface CassiopeiaDefaultProvidersOptions {
  environment: Environment;
  config: CassiopeiaConfig;
  backendEnvironment: EnvironmentResponse;
}

export interface AppConfigOptions {
  config: CassiopeiaConfig;
  backendEnvironment: EnvironmentResponse;
  providers?: Array<Provider | EnvironmentProviders>;
  environment: Environment;
}

/**
 * Creates the default providers that all Cassiopeia apps should include.
 * Apps should use this along with their own routing configuration.
 *
 * Callers fetch `config` (via `fetchCassConfigJSON`) and `backendEnvironment`
 * (via `fetchBackendEnvironment`) in their `main.ts` so the calls happen once.
 */
export const getCassiopeiaDefaultProviders = ({
  environment,
  config,
  backendEnvironment,
}: CassiopeiaDefaultProvidersOptions): Array<Provider | EnvironmentProviders> => {
  initializeTelemetry({
    otel: backendEnvironment.otel,
    deploymentEnvironment: backendEnvironment.deploymentEnvironment,
    companyName: environment.companyName,
    appVersion: environment.version?.tag ?? environment.version?.commit,
  });

  return [
    {
      provide: CUSTOMER_ENVIRONMENT,
      useValue: { ...environment, ...backendEnvironment },
    },
    {
      provide: CASSIOPEIA_CONFIG,
      useValue: signal(config),
    },
    provideAnimations(),
    provideTelemetry(),
    provideHttpClient(withInterceptorsFromDi()),
    ...CassiopeiaServiceProviders,
    importProvidersFrom(
      ServiceWorkerModule.register('ngsw-worker.js', {
        enabled: environment.production,
        registrationStrategy: 'registerWhenStable:30000',
      }),
    ),
    {
      provide: SsoTransferService,
      useClass: SsoTransferService,
    },
    {
      provide: MemberStore,
      useClass: MemberStore,
    },
    provideBannerStore(environment),
    {
      provide: APP_BASE_HREF,
      useFactory: () => {
        const pathname = window.location.pathname.split('/ui')[0];
        const result = pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
        return `${result}/ui`;
      },
    },
    {
      provide: LOCALE_ID,
      useValue: 'da',
    },
    {
      provide: DEFAULT_CURRENCY_CODE,
      useValue: 'DKK',
    },
    AppInitializerProviders,
    DatePipe,
    DecimalPipe,
    TitleCasePipe,
    PercentPipe,
  ];
};
